import { Browser, DeviceType, ProductGridSlotConfig, StringMap, UserData, USER_COOKIE } from '@packages/common';
import { ABTestConfig } from '@packages/shared';
import { GetServerSidePropsContext } from 'next';
import { ParsedUrlQuery } from 'querystring';

import {
  applySortingOnABTestingIndiceUrlParam,
  biFriendlyCookieDecode,
  createAbTestingMiddleware,
  getCookieConfigs,
  setCookies,
} from '../cookie';

import { AppConfig, getAppConfig } from './getAppConfig';
import { getCacheOrCall } from './getCacheOrCall';
import { AffinityMultiplierMap } from './getDyAffinityFilters';

export interface SharedProps {
  abTestConfig: ABTestConfig;
  cookies: StringMap<string>;
  deviceType: DeviceType;
  userIdHash: string | undefined;
  browser: Browser;
  productGridSlotConfig: ProductGridSlotConfig;
  url: string;
  query: ParsedUrlQuery;
  affinitiesConfig: AffinityMultiplierMap;
}

function extractUserIdHash(str?: string): string | undefined {
  if (str) {
    try {
      // first try BI-encoding, then plain stringified object
      const data: UserData = biFriendlyCookieDecode(str) ?? JSON.parse(str);
      return data?.hash;
    } catch (error) {
      console.error(error);
    }
  }
  return '';
}

export const getSharedProps = async (ctx: GetServerSidePropsContext): Promise<SharedProps> => {
  const { req, res, query } = ctx;

  // AB test middleware must be initialized first, as App.getInitialProps uses req.abTestConfig
  const { abTestSetup, affinitiesConfig, productGridSlotConfig } = await getCacheOrCall<AppConfig>({
    _key: 'getAppConfig',
    cb: () => getAppConfig(),
  });

  const abTestMiddleware = createAbTestingMiddleware(abTestSetup);
  const { abTestConfig, abTestCookieConfig, abTestSavedFactorCookieConfig } = abTestMiddleware(req);

  const { getUserAgentData } = await import('../pageUtils/getUserAgentData');
  const { deviceType, browser } = getUserAgentData(req);

  const cookieConfigs = [...getCookieConfigs(req), abTestCookieConfig, abTestSavedFactorCookieConfig];

  setCookies(res, cookieConfigs);

  const cookies = {};
  cookieConfigs.forEach(({ name, value }) => {
    if (value != null) {
      cookies[name] = value;
    }
  });

  const userDataStr = req.cookies?.[USER_COOKIE.name];
  const userIdHash = extractUserIdHash(userDataStr);

  return {
    abTestConfig: applySortingOnABTestingIndiceUrlParam(req, abTestConfig),
    affinitiesConfig,
    browser,
    cookies: {
      ...req.cookies,
      ...cookies,
    },
    deviceType,
    userIdHash,
    productGridSlotConfig,
    url: req.url,
    query,
  };
};
